import {
  EVIDENCE_ROLE_KEYS,
  EXPERIENCE_CONTRACT_VERSION,
  opportunityReviewInputHash,
  recordedTextHash,
  sha256Text,
  updateShadowRecordedText,
} from './aiGuideExperienceContract';
import { validateAiGuideQuality } from './aiGuideQuality';

export const AI_GUIDE_SCHEMA_VERSION = 'sofia_content_treasure.v1';

export const CANONICAL_VISITOR_TOPICS = [
  ['history_heritage', 'History & Heritage'],
  ['architecture_urbanism', 'Architecture & Urban Life'],
  ['art_design', 'Art, Design & Creative Culture'],
  ['people_biographies', 'People & Biographies'],
  ['politics_conflict', 'Power, Politics & Conflict'],
  ['science_technology', 'Science, Engineering & Discovery'],
  ['nature_landscape', 'Nature, Landscape & Ecology'],
  ['maritime_life', 'Sea & Maritime Life'],
  ['local_life', 'Everyday Life, Work & Community'],
  ['food_cuisine', 'Food, Wine & Craft'],
  ['media_storytelling', 'Media, Film & Storytelling'],
  ['mystery_legends', 'Mystery, Legends & Intrigue'],
  ['archaeology_deep_time', 'Archaeology & Deep Time'],
  ['health_leisure_sport', 'Health, Leisure & Sport'],
  ['religion_spirituality', 'Religion, Belief & Sacred Places'],
  ['luxury_lifestyle', 'Luxury, Fashion & Social Life'],
  ['climate_sustainability', 'Climate & Sustainability'],
].map(([id, label]) => ({ id, label }));

const COLLECTIONS = [
  'sources',
  'topics',
  'claims',
  'stops',
  'story_opportunities',
  'story_promises',
  'visitor_topic_prompts',
  'guide_shadow',
];

const text = (value) => String(value ?? '').trim();
const list = (value) => (Array.isArray(value) ? value : []);

export const slugifyAiContentId = (value, fallback = 'item') => {
  const slug = text(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || fallback;
};

const stopName = (point, index) => (
  text(point?.name)
  || text(point?.title)
  || `Stop ${index + 1}`
);

const stopScript = (point) => (
  text(point?.inDepthDescription)
  || text(point?.description)
  || text(point?.shortDescription)
);

const isNarratedStop = (point) => (
  text(point?.point_type || point?.pointType || point?.type) !== 'popup_stop'
);

const getGuideId = (itinerary) => (
  text(itinerary?.metadata?.parent_guide_id)
  || text(itinerary?.parent_guide_id)
  || text(itinerary?.metadata?.id)
  || text(itinerary?.id)
  || `draft_${Date.now()}`
);

export const normalizeAiGuidePackage = (raw) => {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const manifest = value.manifest && typeof value.manifest === 'object'
    ? value.manifest
    : {};
  const contentMode = ['official', 'llm', 'mixed'].includes(manifest.ai_content_mode)
    ? manifest.ai_content_mode
    : (manifest.default_knowledge_policy === 'beyond' ? 'mixed' : 'official');

  return {
    ...value,
    schema_version: text(value.schema_version) || AI_GUIDE_SCHEMA_VERSION,
    manifest: {
      ...manifest,
      treasure_id: text(manifest.treasure_id),
      guide_id: text(manifest.guide_id),
      language: text(manifest.language) || 'en',
      content_version: Number.parseInt(manifest.content_version, 10) || 1,
      ai_content_mode: contentMode,
      default_knowledge_policy: 'official',
      status: text(manifest.status) || 'draft',
      visitor_language_review: {
        ...(manifest.visitor_language_review
          && typeof manifest.visitor_language_review === 'object'
          ? manifest.visitor_language_review
          : {}),
        status: text(manifest.visitor_language_review?.status) || 'draft',
        language: text(manifest.visitor_language_review?.language),
        reviewed_at: text(manifest.visitor_language_review?.reviewed_at),
        reviewer_note: text(manifest.visitor_language_review?.reviewer_note),
      },
    },
    ...Object.fromEntries(COLLECTIONS.map((key) => [key, list(value[key])])),
    governance: {
      ...(value.governance && typeof value.governance === 'object'
        ? value.governance
        : {}),
      forbidden_claims: list(value.governance?.forbidden_claims),
      required_framing: list(value.governance?.required_framing),
      sensitive_topics: list(value.governance?.sensitive_topics),
      pilot_limitations: list(value.governance?.pilot_limitations),
    },
  };
};

export const createAiGuidePackage = (itinerary) => {
  const guideId = getGuideId(itinerary);
  const guideName = text(itinerary?.metadata?.name) || 'Untitled guide';
  const language = text(itinerary?.metadata?.language) || 'en';
  const sourceId = `src_recorded_guide_${slugifyAiContentId(guideId)}`;
  const points = list(itinerary?.points).filter(isNarratedStop);

  const stops = points.map((point, index) => {
    const id = text(point?.id) || `${guideId}_${index + 1}_${language}`;
    return {
      stop_id: id,
      guide_point_index: index,
      names: {
        primary: stopName(point, index),
        aliases: [],
      },
      claim_refs: [],
      visual_cue_refs: [],
      good_followup_questions: [],
      coordinates: {
        latitude: point?.coordinates?.latitude ?? null,
        longitude: point?.coordinates?.longitude ?? null,
      },
    };
  });

  const guideShadow = points.map((point, index) => ({
    stop_id: text(point?.id) || `${guideId}_${index + 1}_${language}`,
    recording_version: 'draft',
    recorded_text: stopScript(point),
    recorded_text_hash: recordedTextHash(stopScript(point)),
    semantic_summary: '',
    claim_refs: [],
    anecdotes: [],
    arguments: [],
    main_reveal: '',
    avoid_repeating: [],
    review_status: 'draft',
    coverage_review_status: 'draft',
    reviewed_text_hash: '',
    coverage_exception_reason: '',
  }));

  return normalizeAiGuidePackage({
    schema_version: AI_GUIDE_SCHEMA_VERSION,
    manifest: {
      treasure_id: `${slugifyAiContentId(guideName)}_${slugifyAiContentId(language)}_v1`,
      guide_id: guideId,
      language,
      content_version: 1,
      experience_contract_version: 1,
      quality_contract_version: 1,
      ai_content_mode: 'official',
      default_knowledge_policy: 'official',
      status: 'draft',
      guide_name: guideName,
      created_at: new Date().toISOString(),
    },
    sources: [
      {
        source_id: sourceId,
        title: `${guideName} recorded guide scripts`,
        source_type: 'partner_guide_script',
        reference: `guide:${guideId}`,
        authority: 'partner_content',
        review_status: 'draft',
        rights: 'partner-provided',
      },
    ],
    topics: [],
    claims: [],
    stops,
    story_opportunities: [],
    story_promises: [],
    visitor_topic_prompts: [],
    guide_shadow: guideShadow,
    governance: {
      forbidden_claims: [],
      required_framing: [
        'Keep evidence, interpretation and visitor prompts distinguishable.',
        'Personalisation must change the evidence and central question, not only tone.',
      ],
      sensitive_topics: [],
      pilot_limitations: [],
    },
  });
};

export const createAiGuideRecord = (collection, packageData) => {
  const suffix = Date.now().toString(36);
  const firstStopId = packageData?.stops?.[0]?.stop_id || '';
  const firstSourceId = packageData?.sources?.[0]?.source_id || '';

  switch (collection) {
    case 'sources':
      return {
        source_id: `src_${suffix}`,
        title: '',
        source_type: 'partner_material',
        reference: '',
        authority: 'partner_content',
        review_status: 'draft',
        rights: '',
      };
    case 'topics':
      return { topic_id: `topic_${suffix}`, label: '' };
    case 'claims':
      return {
        claim_id: `claim_${suffix}`,
        text: '',
        claim_type: 'fact',
        source_refs: firstSourceId ? [firstSourceId] : [],
        source_locator: '',
        source_excerpt: '',
        topic_ids: [],
        stop_ids: firstStopId ? [firstStopId] : [],
        confidence: 'unreviewed',
        review_status: 'draft',
        official_safe: false,
        why_it_matters: '',
      };
    case 'story_opportunities':
      return {
        opportunity_id: `opportunity_${suffix}`,
        discovery_title: '',
        teaser: '',
        central_question: '',
        anchor_stop_ids: [],
        claim_refs: [],
        topic_ids: [],
        canonical_topic_weights: {},
        motif_weights: {},
        editorial_priority: 0.5,
        narrative_value: '',
        experience_contribution: '',
        experience_review: {
          status: 'draft',
          reviewed_input_hash: '',
          reviewed_at: '',
        },
        evidence_roles: {},
        personalization_mode: 'universal',
        visitor_lenses: [],
        visual_cues: [],
      };
    case 'story_promises':
      return {
        promise_id: `promise_${suffix}`,
        label: '',
        description: '',
        topic_ids: [],
        opportunity_ids: [],
        stated_interests: [],
        primary_question: '',
        secondary_questions: [],
        likes: [],
        strong_signals: [],
      };
    case 'visitor_topic_prompts':
      return {
        topic_prompt_id: `visitor_topic_${suffix}`,
        title: '',
        description: '',
        canonical_topic_ids: [],
        promise_ids: [],
        places: [],
      };
    case 'guide_shadow':
      return {
        stop_id: firstStopId,
        recording_version: 'draft',
        recorded_text: '',
        recorded_text_hash: recordedTextHash(''),
        semantic_summary: '',
        claim_refs: [],
        anecdotes: [],
        arguments: [],
        main_reveal: '',
        avoid_repeating: [],
        review_status: 'draft',
        coverage_review_status: 'draft',
        reviewed_text_hash: '',
        coverage_exception_reason: '',
      };
    default:
      throw new Error(`Unsupported AI guide collection: ${collection}`);
  }
};

const uniqueIds = (items, key, issues, path, label) => {
  const ids = new Set();
  list(items).forEach((item, index) => {
    const id = text(item?.[key]);
    if (!id) {
      issues.push({
        severity: 'error',
        path: `${path}[${index}].${key}`,
        message: `${label} ID is required.`,
      });
      return;
    }
    if (ids.has(id)) {
      issues.push({
        severity: 'error',
        path: `${path}[${index}].${key}`,
        message: `Duplicate ${label.toLowerCase()} ID: ${id}.`,
      });
    }
    ids.add(id);
  });
  return ids;
};

const checkRefs = (issues, owner, refs, known, kind, path, required = false) => {
  const values = list(refs).map(text).filter(Boolean);
  if (required && values.length === 0) {
    issues.push({
      severity: 'error',
      path,
      message: `${owner} requires at least one ${kind} reference.`,
    });
  }
  values.forEach((id) => {
    if (!known.has(id)) {
      issues.push({
        severity: 'error',
        path,
        message: `${owner} references unknown ${kind} "${id}".`,
      });
    }
  });
};

export const validateAiGuidePackage = (raw) => {
  const data = normalizeAiGuidePackage(raw);
  const issues = [];
  const requiredManifestFields = ['treasure_id', 'guide_id', 'language'];
  const experienceContractVersion = data.manifest.experience_contract_version;
  const usesExperienceContract = experienceContractVersion === EXPERIENCE_CONTRACT_VERSION;

  if (experienceContractVersion === undefined || experienceContractVersion === null) {
    issues.push({
      severity: 'warning',
      path: 'manifest.experience_contract_version',
      message: 'Legacy experience contract: contribution and recording-coverage reviews are recommended but not required for publication.',
    });
  } else if (!usesExperienceContract) {
    issues.push({
      severity: 'error',
      path: 'manifest.experience_contract_version',
      message: `Experience contract version "${experienceContractVersion}" is not supported by this Builder. The package remains readable, but publication is blocked to avoid treating unknown review fields as approved.`,
    });
  }

  if (!data.schema_version.startsWith('sofia_content_treasure.')) {
    issues.push({
      severity: 'error',
      path: 'schema_version',
      message: `Unsupported schema "${data.schema_version}".`,
    });
  }

  requiredManifestFields.forEach((field) => {
    if (!text(data.manifest[field])) {
      issues.push({
        severity: 'error',
        path: `manifest.${field}`,
        message: `${field} is required.`,
      });
    }
  });

  if (usesExperienceContract) {
    const languageReview = data.manifest.visitor_language_review;
    if (languageReview.status !== 'reviewed'
        || languageReview.language !== data.manifest.language
        || !languageReview.reviewed_at) {
      issues.push({
        severity: 'error',
        path: 'manifest.visitor_language_review',
        message: 'Visitor-facing titles, prompts, promises, questions, lens copy, and follow-ups must be reviewed in the current package language.',
      });
    }
  }

  if (!Number.isInteger(data.manifest.content_version)
      || data.manifest.content_version < 1) {
    issues.push({
      severity: 'error',
      path: 'manifest.content_version',
      message: 'Content version must be a positive integer.',
    });
  }

  const sourceIds = uniqueIds(data.sources, 'source_id', issues, 'sources', 'Source');
  const topicIds = uniqueIds(data.topics, 'topic_id', issues, 'topics', 'Topic');
  const claimIds = uniqueIds(data.claims, 'claim_id', issues, 'claims', 'Claim');
  const stopIds = uniqueIds(data.stops, 'stop_id', issues, 'stops', 'Stop');
  const opportunityIds = uniqueIds(
    data.story_opportunities,
    'opportunity_id',
    issues,
    'story_opportunities',
    'Story opportunity',
  );
  const promiseIds = uniqueIds(
    data.story_promises,
    'promise_id',
    issues,
    'story_promises',
    'Story promise',
  );
  uniqueIds(
    data.visitor_topic_prompts,
    'topic_prompt_id',
    issues,
    'visitor_topic_prompts',
    'Visitor topic prompt',
  );
  const canonicalIds = new Set(CANONICAL_VISITOR_TOPICS.map((topic) => topic.id));

  if (data.sources.length === 0) {
    issues.push({
      severity: 'error',
      path: 'sources',
      message: 'At least one registered source is required.',
    });
  }
  if (data.story_promises.length === 0) {
    issues.push({
      severity: 'error',
      path: 'story_promises',
      message: 'AI5 requires at least one story promise.',
    });
  }
  if (data.visitor_topic_prompts.length === 0) {
    issues.push({
      severity: 'error',
      path: 'visitor_topic_prompts',
      message: 'AI5 requires at least one visitor topic prompt.',
    });
  }
  const requiredOpportunities = data.manifest.ai_content_mode === 'official' ? 5 : 4;
  if (data.story_opportunities.length < requiredOpportunities) {
    issues.push({
      severity: 'error',
      path: 'story_opportunities',
      message: `${data.manifest.ai_content_mode === 'official' ? 'Official-only' : 'AI5'} mode requires at least ${requiredOpportunities} story opportunities.`,
    });
  }

  data.claims.forEach((claim, index) => {
    const owner = `Claim ${text(claim.claim_id) || index + 1}`;
    if (!text(claim.text)) {
      issues.push({
        severity: 'error',
        path: `claims[${index}].text`,
        message: `${owner} has no assertion text.`,
      });
    }
    checkRefs(
      issues,
      owner,
      claim.source_refs,
      sourceIds,
      'source',
      `claims[${index}].source_refs`,
      true,
    );
    checkRefs(
      issues,
      owner,
      claim.topic_ids,
      topicIds,
      'topic',
      `claims[${index}].topic_ids`,
    );
    checkRefs(
      issues,
      owner,
      claim.stop_ids,
      stopIds,
      'stop',
      `claims[${index}].stop_ids`,
    );
  });

  data.story_opportunities.forEach((opportunity, index) => {
    const owner = `Story opportunity ${text(opportunity.opportunity_id) || index + 1}`;
    ['discovery_title', 'teaser', 'central_question'].forEach((field) => {
      if (!text(opportunity[field])) {
        issues.push({
          severity: 'error',
          path: `story_opportunities[${index}].${field}`,
          message: `${owner} requires ${field}.`,
        });
      }
    });
    checkRefs(
      issues,
      owner,
      opportunity.claim_refs,
      claimIds,
      'claim',
      `story_opportunities[${index}].claim_refs`,
      true,
    );
    checkRefs(
      issues,
      owner,
      opportunity.anchor_stop_ids,
      stopIds,
      'stop',
      `story_opportunities[${index}].anchor_stop_ids`,
    );
    checkRefs(
      issues,
      owner,
      opportunity.topic_ids,
      topicIds,
      'topic',
      `story_opportunities[${index}].topic_ids`,
    );
    if (Object.keys(opportunity.canonical_topic_weights || {}).length === 0) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].canonical_topic_weights`,
        message: `${owner} needs at least one canonical visitor-topic weight.`,
      });
    }
    if (Object.keys(opportunity.motif_weights || {}).length === 0) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].motif_weights`,
        message: `${owner} needs at least one narrative motif weight.`,
      });
    }
    const contribution = text(opportunity.experience_contribution);
    if (contribution.length > 800) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].experience_contribution`,
        message: `${owner} experience contribution exceeds 800 characters.`,
      });
    }
    if (usesExperienceContract && !contribution) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].experience_contribution`,
        message: `${owner} requires an intended new visitor understanding for experience contract v1.`,
      });
    }
    const experienceReview = opportunity.experience_review || {};
    const currentReviewHash = opportunityReviewInputHash(opportunity, data.guide_shadow);
    const reviewIsCurrent = experienceReview.status === 'reviewed'
      && text(experienceReview.reviewed_input_hash) === currentReviewHash
      && Boolean(text(experienceReview.reviewed_at));
    if (contribution && !reviewIsCurrent) {
      issues.push({
        severity: usesExperienceContract ? 'error' : 'warning',
        path: `story_opportunities[${index}].experience_review`,
        message: `${owner} contribution is unreviewed or stale for its current question, evidence, and recording coverage.`,
      });
    }
    const evidenceRoles = opportunity.evidence_roles;
    if (evidenceRoles !== undefined
        && (!evidenceRoles || typeof evidenceRoles !== 'object' || Array.isArray(evidenceRoles))) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].evidence_roles`,
        message: `${owner} evidence roles must be an object.`,
      });
    } else if (evidenceRoles) {
      const opportunityClaimIds = new Set(list(opportunity.claim_refs).map(text).filter(Boolean));
      Object.entries(evidenceRoles).forEach(([role, refs]) => {
        if (!EVIDENCE_ROLE_KEYS.includes(role)) {
          issues.push({
            severity: 'error',
            path: `story_opportunities[${index}].evidence_roles.${role}`,
            message: `${owner} has unsupported evidence role "${role}".`,
          });
          return;
        }
        if (!Array.isArray(refs)) {
          issues.push({
            severity: 'error',
            path: `story_opportunities[${index}].evidence_roles.${role}`,
            message: `${owner} evidence role "${role}" must contain claim references.`,
          });
          return;
        }
        checkRefs(
          issues,
          owner,
          refs,
          claimIds,
          'claim',
          `story_opportunities[${index}].evidence_roles.${role}`,
        );
        list(refs).map(text).filter(Boolean).forEach((claimId) => {
          if (!opportunityClaimIds.has(claimId)) {
            issues.push({
              severity: 'error',
              path: `story_opportunities[${index}].evidence_roles.${role}`,
              message: `${owner} evidence role "${role}" references claim "${claimId}" outside claim_refs.`,
            });
          }
        });
      });
    }
    const personalizationMode = text(opportunity.personalization_mode);
    if (personalizationMode && !['universal', 'lenses'].includes(personalizationMode)) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].personalization_mode`,
        message: `${owner} has an unsupported personalization mode.`,
      });
    }
    if (usesExperienceContract && !['universal', 'lenses'].includes(personalizationMode)) {
      issues.push({
        severity: 'error',
        path: `story_opportunities[${index}].personalization_mode`,
        message: `${owner} must be explicitly universal or use reviewed visitor lenses.`,
      });
    }
    const lenses = list(opportunity.visitor_lenses);
    if (personalizationMode === 'lenses' && lenses.length === 0) {
      issues.push({
        severity: usesExperienceContract ? 'error' : 'warning',
        path: `story_opportunities[${index}].visitor_lenses`,
        message: `${owner} uses lens personalization but has no visitor lenses.`,
      });
    }
    const lensIds = new Set();
    const lensSignatures = new Set();
    lenses.forEach((lens, lensIndex) => {
      const path = `story_opportunities[${index}].visitor_lenses[${lensIndex}]`;
      const lensId = text(lens?.lens_id);
      if (!lensId || lensIds.has(lensId)) {
        issues.push({
          severity: 'error',
          path: `${path}.lens_id`,
          message: `${owner} visitor lenses require unique IDs.`,
        });
      }
      lensIds.add(lensId);
      ['central_question', 'experience_contribution', 'fit_reason'].forEach((field) => {
        if (!text(lens?.[field])) {
          issues.push({
            severity: 'error',
            path: `${path}.${field}`,
            message: `${owner} lens ${lensId || lensIndex + 1} requires ${field}.`,
          });
        }
      });
      if (text(lens?.experience_contribution).length > 800) {
        issues.push({
          severity: 'error',
          path: `${path}.experience_contribution`,
          message: `${owner} lens ${lensId || lensIndex + 1} contribution exceeds 800 characters.`,
        });
      }
      if (!['brief', 'standard', 'deep'].includes(text(lens?.depth))) {
        issues.push({
          severity: 'error',
          path: `${path}.depth`,
          message: `${owner} lens ${lensId || lensIndex + 1} has invalid depth.`,
        });
      }
      const weights = lens?.canonical_topic_weights;
      if (!weights || typeof weights !== 'object' || Array.isArray(weights)
          || Object.keys(weights).length === 0) {
        issues.push({
          severity: 'error',
          path: `${path}.canonical_topic_weights`,
          message: `${owner} lens ${lensId || lensIndex + 1} needs visitor-topic weights.`,
        });
      } else {
        Object.entries(weights).forEach(([topicId, weight]) => {
          if (!canonicalIds.has(topicId)
              || !Number.isFinite(Number(weight))
              || Number(weight) < 0
              || Number(weight) > 1) {
            issues.push({
              severity: 'error',
              path: `${path}.canonical_topic_weights.${topicId}`,
              message: `${owner} lens ${lensId || lensIndex + 1} has an invalid topic weight.`,
            });
          }
        });
      }
      checkRefs(
        issues,
        `${owner} lens ${lensId || lensIndex + 1}`,
        lens?.claim_refs,
        claimIds,
        'claim',
        `${path}.claim_refs`,
        true,
      );
      list(lens?.claim_refs).map(text).filter(Boolean).forEach((claimId) => {
        if (!new Set(list(opportunity.claim_refs).map(text)).has(claimId)) {
          issues.push({
            severity: 'error',
            path: `${path}.claim_refs`,
            message: `${owner} lens ${lensId || lensIndex + 1} references claim "${claimId}" outside claim_refs.`,
          });
        }
      });
      const signature = JSON.stringify({
        question: text(lens?.central_question).toLowerCase(),
        claims: list(lens?.claim_refs).map(text).filter(Boolean).sort(),
        depth: text(lens?.depth),
      });
      if (lensSignatures.has(signature)) {
        issues.push({
          severity: 'warning',
          path,
          message: `${owner} has nominally different lenses with the same question, evidence, and depth. Use universal mode or make the explanation meaningfully different.`,
        });
      }
      lensSignatures.add(signature);
    });
  });

  data.story_promises.forEach((promise, index) => {
    const owner = `Story promise ${text(promise.promise_id) || index + 1}`;
    if (!text(promise.label) || !text(promise.description)) {
      issues.push({
        severity: 'error',
        path: `story_promises[${index}]`,
        message: `${owner} requires a label and description.`,
      });
    }
    checkRefs(
      issues,
      owner,
      promise.opportunity_ids,
      opportunityIds,
      'story opportunity',
      `story_promises[${index}].opportunity_ids`,
      true,
    );
    checkRefs(
      issues,
      owner,
      promise.topic_ids,
      topicIds,
      'topic',
      `story_promises[${index}].topic_ids`,
    );
  });

  data.visitor_topic_prompts.forEach((prompt, index) => {
    const owner = `Visitor topic prompt ${text(prompt.topic_prompt_id) || index + 1}`;
    if (!text(prompt.title) || !text(prompt.description)) {
      issues.push({
        severity: 'error',
        path: `visitor_topic_prompts[${index}]`,
        message: `${owner} requires a title and plain-language description.`,
      });
    }
    checkRefs(
      issues,
      owner,
      prompt.canonical_topic_ids,
      canonicalIds,
      'canonical topic',
      `visitor_topic_prompts[${index}].canonical_topic_ids`,
      true,
    );
    checkRefs(
      issues,
      owner,
      prompt.promise_ids,
      promiseIds,
      'story promise',
      `visitor_topic_prompts[${index}].promise_ids`,
      true,
    );
  });

  data.guide_shadow.forEach((shadow, index) => {
    const owner = `Guide Shadow record ${index + 1}`;
    checkRefs(
      issues,
      owner,
      shadow.stop_id ? [shadow.stop_id] : [],
      stopIds,
      'stop',
      `guide_shadow[${index}].stop_id`,
      true,
    );
    checkRefs(
      issues,
      owner,
      shadow.claim_refs,
      claimIds,
      'claim',
      `guide_shadow[${index}].claim_refs`,
    );
    if (!text(shadow.semantic_summary)) {
      issues.push({
        severity: 'warning',
        path: `guide_shadow[${index}].semantic_summary`,
        message: `${owner} still needs a reviewed semantic summary.`,
      });
    }
    const currentTextHash = recordedTextHash(shadow.recorded_text);
    if (text(shadow.recorded_text_hash) !== currentTextHash) {
      issues.push({
        severity: usesExperienceContract ? 'error' : 'warning',
        path: `guide_shadow[${index}].recorded_text_hash`,
        message: `${owner} script hash is missing or does not match the current recorded script.`,
      });
    }
    const coverageStatus = text(shadow.coverage_review_status) || 'draft';
    if (!['draft', 'reviewed', 'not_applicable'].includes(coverageStatus)) {
      issues.push({
        severity: 'error',
        path: `guide_shadow[${index}].coverage_review_status`,
        message: `${owner} has an unsupported coverage review status.`,
      });
    }
    const hasMappedCoverage = Boolean(
      text(shadow.semantic_summary)
      || list(shadow.claim_refs).map(text).some(Boolean)
      || list(shadow.anecdotes).map(text).some(Boolean)
      || list(shadow.arguments).map(text).some(Boolean)
      || text(shadow.main_reveal)
      || list(shadow.avoid_repeating).map(text).some(Boolean)
    );
    const hasException = Boolean(text(shadow.coverage_exception_reason));
    const reviewIsCurrent = ['reviewed', 'not_applicable'].includes(coverageStatus)
      && text(shadow.reviewed_text_hash) === currentTextHash;
    if (coverageStatus === 'reviewed' && !hasMappedCoverage) {
      issues.push({
        severity: usesExperienceContract ? 'error' : 'warning',
        path: `guide_shadow[${index}].coverage_review_status`,
        message: `${owner} cannot be treated as reviewed while every coverage field is empty.`,
      });
    }
    if (coverageStatus === 'not_applicable' && !hasException) {
      issues.push({
        severity: usesExperienceContract ? 'error' : 'warning',
        path: `guide_shadow[${index}].coverage_exception_reason`,
        message: `${owner} needs a reviewed explanation when coverage mapping is not applicable.`,
      });
    }
    if (usesExperienceContract && !reviewIsCurrent) {
      issues.push({
        severity: 'error',
        path: `guide_shadow[${index}].reviewed_text_hash`,
        message: `${owner} coverage review is missing or stale for the current recorded script.`,
      });
    }
  });

  issues.push(...validateAiGuideQuality(data, sha256Text));
  const errors = issues.filter((issue) => issue.severity === 'error').length;
  const warnings = issues.filter((issue) => issue.severity === 'warning').length;
  return { data, issues, errors, warnings, valid: errors === 0 };
};

export const aiGuidePackageFilename = (packageData) => {
  const manifest = packageData?.manifest || {};
  const base = slugifyAiContentId(
    manifest.treasure_id || manifest.guide_name || manifest.guide_id,
    'sofia_content_treasure',
  );
  return `${base}.json`;
};

const mergeUniqueRecords = (current, incoming, idKey) => {
  const existingIds = new Set((current || []).map((item) => text(item?.[idKey])));
  return [
    ...(current || []),
    ...(incoming || []).filter((item) => {
      const id = text(item?.[idKey]);
      return id && !existingIds.has(id);
    }),
  ];
};

export const mergeAiGuideResearchDraft = (
  packageData,
  researchResult,
) => {
  const draft = researchResult?.draft || {};
  const currentShadow = new Map(
    (packageData.guide_shadow || []).map((shadow) => [text(shadow.stop_id), shadow]),
  );

  for (const update of draft.shadow_updates || []) {
    const stopId = text(update.stop_id);
    if (!stopId) continue;
    const existing = currentShadow.get(stopId) || {
      stop_id: stopId,
      recording_version: 'draft',
      recorded_text: '',
    };
    currentShadow.set(stopId, {
      ...existing,
      ...update,
      recorded_text: existing.recorded_text || '',
      review_status: 'draft',
    });
  }

  return normalizeAiGuidePackage({
    ...packageData,
    sources: mergeUniqueRecords(
      packageData.sources,
      draft.sources,
      'source_id',
    ),
    topics: mergeUniqueRecords(
      packageData.topics,
      draft.topics,
      'topic_id',
    ),
    claims: mergeUniqueRecords(
      packageData.claims,
      draft.claims,
      'claim_id',
    ),
    story_opportunities: mergeUniqueRecords(
      packageData.story_opportunities,
      draft.story_opportunities,
      'opportunity_id',
    ),
    story_promises: mergeUniqueRecords(
      packageData.story_promises,
      draft.story_promises,
      'promise_id',
    ),
    visitor_topic_prompts: mergeUniqueRecords(
      packageData.visitor_topic_prompts,
      draft.visitor_topic_prompts,
      'topic_prompt_id',
    ),
    guide_shadow: [...currentShadow.values()],
    research_runs: [
      ...(packageData.research_runs || []),
      {
        research_run_id: researchResult.researchRunId,
        model: researchResult.model,
        created_at: new Date().toISOString(),
        summary: draft.research_summary || '',
        consulted_sources: researchResult.consultedSources || [],
        usage: researchResult.usage || null,
      },
    ],
  });
};

export const syncAiGuideShadowRecordedText = (shadow, recordedText) => (
  updateShadowRecordedText(shadow, recordedText)
);
